import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="s3-upload"
// Base controller for S3 uploads - can be extended by other controllers
export default class extends Controller {
  static targets = ["fileInput", "submitButton", "progressBar", "progressPercent", "progressContainer", "processingContainer", "form", "s3KeyInput", "originalFilenameInput", "errorContainer", "errorMessage"]
  static values = {
    presignedUrl: String,
    tableDefinitionId: String,
    // When set, the uploaded file is described before the form is submitted, so
    // formats that need a choice (which sheet? which cells?) can ask for one.
    previewUrl: String
  }

  connect() {
    console.log("S3 Upload controller connected")
    this.uploadComplete = false
  }

  // True once the file is in S3, whether or not this controller instance is the
  // one that put it there.
  get alreadyUploaded() {
    if (this.uploadComplete) return true

    return this.hasS3KeyInputTarget && this.s3KeyInputTarget.value.trim() !== ""
  }

  // Override this getter in subclasses to customize the presigned upload URL
  get presignedUploadUrl() {
    return `/table_definitions/${this.tableDefinitionIdValue}/import_mappings/presigned_upload`
  }

  // Override these getters in subclasses to customize button text
  get uploadingText() {
    return "Uploading..."
  }

  get processingText() {
    return "Processing..."
  }

  get defaultButtonText() {
    return "Create Import Mapping"
  }

  async handleSubmit(event) {
    // If upload is already complete, let the form submit through normally.
    //
    // The hidden s3_key field is checked as well as the in-memory flag: the
    // file input is deliberately cleared once the file is in S3, so if this
    // controller is ever re-instantiated between the upload and the submit
    // (the picker step leaves the page sitting idle in between), the flag is
    // lost while the key is not, and the form would otherwise be refused with
    // "Please select a file" for a file that is already uploaded.
    if (this.alreadyUploaded) {
      this.uploadComplete = true
      return
    }

    event.preventDefault()
    event.stopPropagation()

    const file = this.fileInputTarget.files[0]
    if (!file) {
      this.showError("Please select a file")
      return
    }

    // Validate file size (100MB max)
    const maxSize = 100 * 1024 * 1024 // 100MB
    if (file.size > maxSize) {
      this.showError("File is too large. Maximum size is 100MB")
      return
    }

    try {
      this.clearError()
      // Disable submit button and show progress
      this.submitButtonTarget.disabled = true
      this.submitButtonTarget.textContent = this.uploadingText
      this.showProgress()

      // Get presigned URL from server
      const presignResponse = await fetch(this.presignedUploadUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({
          filename: file.name,
          content_type: file.type
        })
      })

      if (!presignResponse.ok) {
        throw new Error('Failed to get presigned URL')
      }

      const { url, key, method } = await presignResponse.json()
      console.info('Received presigned upload URL', {
        uploadHost: new URL(url).host,
        method,
        key
      })

      // Upload directly to S3
      const uploadResponse = await this.uploadToS3(url, file, method)

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload file to S3')
      }

      // Set S3 key and original filename in hidden fields
      this.s3KeyInputTarget.value = key
      this.originalFilenameInputTarget.value = file.name

      // Clear file input so the browser doesn't re-upload the file to Rails
      this.fileInputTarget.value = ""

      // Transition from upload progress to schema analysis indicator
      this.hideProgress()
      this.showProcessing()

      // Some formats need the user to choose what to import before the schema
      // can be inferred at all - an Excel workbook has no single obvious table.
      if (this.hasPreviewUrlValue && this.previewUrlValue) {
        const preview = await this.fetchPreview({ s3_key: key, original_filename: file.name })

        if (preview && preview.previewable) {
          this.hideProcessing()
          this.submitButtonTarget.disabled = false
          this.submitButtonTarget.textContent = this.defaultButtonText
          // The file is in S3 and the file input has been cleared, so the next
          // submit - the one the picker's Continue button fires - must pass
          // straight through rather than trying to upload a file again.
          this.uploadComplete = true
          // Handed to the file-preview controller on this same element.
          this.dispatch("preview", {
            prefix: "fileupload",
            detail: { preview, s3Key: key, filename: file.name, previewUrl: this.previewUrlValue }
          })
          return
        }
      }

      // Submit the form to Rails with the S3 key
      // Use requestSubmit() so Turbo intercepts the submission correctly
      // (native .submit() bypasses Turbo and gets silently dropped)
      // Re-enable the button first: a disabled submit button causes requestSubmit() to be ignored
      this.submitButtonTarget.textContent = this.processingText
      this.submitButtonTarget.disabled = false
      this.uploadComplete = true
      this.formTarget.requestSubmit()

    } catch (error) {
      console.error('Upload error:', error)
      this.showError(error.message)
      this.submitButtonTarget.disabled = false
      this.submitButtonTarget.textContent = this.defaultButtonText
      this.hideProgress()
      this.hideProcessing()
    }
  }

  // Ask the server what is inside the uploaded file. A 422 carries a
  // user-facing message and a machine-readable code; anything else is a bug and
  // is reported generically rather than leaking internals.
  async fetchPreview(body) {
    const response = await fetch(this.previewUrlValue, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
      },
      body: JSON.stringify(body)
    })

    const payload = await response.json().catch(() => null)

    if (!response.ok) {
      throw new Error((payload && payload.error) || 'This file could not be analysed.')
    }

    return payload
  }

  showError(message) {
    if (this.hasErrorContainerTarget && this.hasErrorMessageTarget) {
      this.errorMessageTarget.textContent = message
      this.errorContainerTarget.classList.remove('hidden')
      this.errorContainerTarget.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    } else {
      // No error panel on this page yet - better a dialog than a silent failure.
      alert(message)
    }
  }

  clearError() {
    if (this.hasErrorContainerTarget) {
      this.errorContainerTarget.classList.add('hidden')
    }
  }

  async uploadToS3(url, file, method) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest()
      const uploadUrl = new URL(url)

      // Track upload progress
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const percentComplete = (e.loaded / e.total) * 100
          this.updateProgress(percentComplete)
        }
      })

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve({ ok: true })
        } else {
          reject(new Error(`S3 upload failed with status ${xhr.status} (${uploadUrl.host})`))
        }
      })

      xhr.addEventListener('error', () => {
        console.error('S3 upload network error', {
          url,
          host: uploadUrl.host,
          readyState: xhr.readyState,
          status: xhr.status
        })
        reject(new Error(`Network error during S3 upload to ${uploadUrl.host}. Check browser console for CORS/preflight details.`))
      })

      xhr.addEventListener('abort', () => {
        reject(new Error(`S3 upload was aborted for ${uploadUrl.host}`))
      })

      xhr.open(method, url)
      xhr.setRequestHeader('Content-Type', file.type)

      if (this.requiresTunnelBypassHeader(uploadUrl)) {
        xhr.setRequestHeader('bypass-tunnel-reminder', '1')
      }

      xhr.send(file)
    })
  }

  requiresTunnelBypassHeader(uploadUrl) {
    return ["localhost", "127.0.0.1", "localstack"].some((host) => uploadUrl.hostname.includes(host))
  }

  showProgress() {
    this.progressContainerTarget.classList.remove('hidden')
  }

  hideProgress() {
    this.progressContainerTarget.classList.add('hidden')
    this.updateProgress(0)
  }

  showProcessing() {
    if (this.hasProcessingContainerTarget) {
      this.processingContainerTarget.classList.remove('hidden')
    }
  }

  hideProcessing() {
    if (this.hasProcessingContainerTarget) {
      this.processingContainerTarget.classList.add('hidden')
    }
  }

  updateProgress(percent) {
    this.progressBarTarget.style.width = `${percent}%`
    this.progressBarTarget.setAttribute('aria-valuenow', percent)
    if (this.hasProgressPercentTarget) {
      this.progressPercentTarget.textContent = Math.round(percent)
    }
  }

  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]').content
  }
}
